
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Textarea } from '../ui/textarea';
import { useTeacherAttendance } from '../../hooks/useTeacherAttendance';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useTimetableStore } from '../../store/timetableStore';

interface AttendanceStatusDialogProps {
  isOpen: boolean;
  onClose: () => void;
  teacher: {
    id: string;
    name: string;
    attendanceStatus: 'present' | 'absent' | 'late';
    reason?: string;
    hasSubstitute?: boolean;
    substituteName?: string;
  };
}

const AttendanceStatusDialog = ({ isOpen, onClose, teacher }: AttendanceStatusDialogProps) => {
  const [status, setStatus] = useState(teacher.attendanceStatus);
  const [reason, setReason] = useState(teacher.reason || '');
  const [needsSubstitute, setNeedsSubstitute] = useState(teacher.hasSubstitute || false);
  const [substituteId, setSubstituteId] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [showSubstituteSelect, setShowSubstituteSelect] = useState(teacher.hasSubstitute || false);
  
  const { markTeacherAttendance } = useTeacherAttendance();
  const { timetableData } = useTimetableStore();
  
  // Get list of available teachers for substitution (exclude current teacher)
  const availableSubstitutes = timetableData
    ? timetableData.filter(t => t.id !== teacher.id)
    : [];
  
  const handleStatusChange = (value: string) => {
    setStatus(value as 'present' | 'absent' | 'late');
    // Only show substitute option for absent or late
    setShowSubstituteSelect(value === 'absent' || value === 'late');
    if (value === 'present') {
      setNeedsSubstitute(false);
    }
  };
  
  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      const substituteName = needsSubstitute && substituteId 
        ? availableSubstitutes.find(t => t.id === substituteId)?.name
        : undefined;
      
      // Mark attendance with synchronized timetable updates
      await markTeacherAttendance(
        teacher.id,
        teacher.name,
        status,
        reason || undefined
      );
      
      // If substitute needed and selected, assign them
      if (needsSubstitute && substituteId && substituteName) {
        // Additional substitute assignment would go here
        toast.success(`${substituteName} assigned as substitute for ${teacher.name}`);
      }
      
      onClose();
    } catch (error) {
      console.error('Error updating attendance status:', error);
      toast.error('Failed to update attendance status');
    } finally {
      setIsUpdating(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Update Attendance Status</DialogTitle>
          <DialogDescription>
            Change attendance status for {teacher.name}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Attendance Status</Label>
            <RadioGroup 
              value={status} 
              onValueChange={handleStatusChange}
              className="flex flex-col space-y-1"
            >
              <div className="flex items-center space-x-3 rounded-md border p-3 bg-green-50">
                <RadioGroupItem value="present" id="present" />
                <Label htmlFor="present" className="font-medium text-green-700">Present</Label>
              </div>
              <div className="flex items-center space-x-3 rounded-md border p-3 bg-yellow-50">
                <RadioGroupItem value="late" id="late" />
                <Label htmlFor="late" className="font-medium text-yellow-700">Late</Label>
              </div>
              <div className="flex items-center space-x-3 rounded-md border p-3 bg-red-50">
                <RadioGroupItem value="absent" id="absent" />
                <Label htmlFor="absent" className="font-medium text-red-700">Absent</Label>
              </div>
            </RadioGroup>
          </div>
          
          {status !== 'present' && (
            <div className="space-y-2">
              <Label htmlFor="reason">Reason (Optional)</Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder={`Reason for being ${status}`}
                className="min-h-[80px]"
              />
            </div>
          )}
          
          {showSubstituteSelect && (
            <div className="space-y-3 pt-2">
              <div className="flex items-center space-x-2">
                <input 
                  type="checkbox" 
                  id="needsSubstitute" 
                  checked={needsSubstitute} 
                  onChange={e => setNeedsSubstitute(e.target.checked)}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <Label htmlFor="needsSubstitute">Needs Substitute</Label>
              </div>
              
              {needsSubstitute && (
                <div className="space-y-2">
                  <Label>Select Substitute Teacher</Label>
                  <Select value={substituteId} onValueChange={setSubstituteId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a substitute" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSubstitutes.map(teacher => (
                        <SelectItem key={teacher.id} value={teacher.id}>
                          {teacher.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button onClick={handleUpdate} disabled={isUpdating}>
            {isUpdating ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AttendanceStatusDialog;
